/**
 * User UI preferences — types, defaults e paleta curada de accent.
 * Espelha o shape salvo em profiles.preferences (jsonb).
 *
 * A paleta aqui DEVE estar em sincronia com ALLOWED_ACCENTS no backend
 * (backend/app/routers/me.py).
 */

export type ThemeMode  = "light" | "dark" | "system" | "auto";
export type LocaleCode = "pt" | "en" | "es";

export interface UserPreferences {
  theme?:  ThemeMode;
  accent?: string;
  locale?: LocaleCode;
}

export const DEFAULT_PREFERENCES: Required<UserPreferences> = {
  theme:  "dark",
  accent: "#25954b",
  locale: "pt",
};

// Modo "auto" alterna pelo horário do dia. Hora local do navegador.
// 6h-18h → claro; restante → escuro.
export const AUTO_DAY_START_HOUR = 6;
export const AUTO_DAY_END_HOUR   = 18;

export interface AccentColor {
  hex:  string;
  name: string;
}

export const ACCENT_PALETTE: AccentColor[] = [
  { hex: "#25954b", name: "Verde Miller" },
  { hex: "#0ea5e9", name: "Ciano"        },
  { hex: "#6366f1", name: "Índigo"       },
  { hex: "#ec4899", name: "Rosa"         },
  { hex: "#f97316", name: "Laranja"      },
  { hex: "#14b8a6", name: "Teal"         },
];

export function isValidAccent(hex: string): boolean {
  return ACCENT_PALETTE.some(c => c.hex === hex);
}

export function accentSoft(hex: string, alpha = 0.18): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Resolve um ThemeMode pra "light" | "dark" considerando o contexto atual:
 *  - system: usa prefers-color-scheme do SO
 *  - auto:   usa hora local (6h-18h = light)
 *  - light/dark: passa direto
 */
export function resolveTheme(mode: ThemeMode, now: Date = new Date()): "light" | "dark" {
  if (mode === "light" || mode === "dark") return mode;
  if (mode === "auto") {
    const h = now.getHours();
    return (h >= AUTO_DAY_START_HOUR && h < AUTO_DAY_END_HOUR) ? "light" : "dark";
  }
  // system
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
